import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Banknote, CreditCard, History, Minus, Phone, Plus,
  ScanBarcode, Search, ShoppingCart, Ticket, Trash2, Undo2, Redo2, Wallet, X,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { useProducts } from '../../context/ProductsContext';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import useSessionDraft from '../../hooks/useSessionDraft';
import { formatCurrency } from '../../data/mockData';
import {
  getProduct, listProducts, lookupByBarcode, listLots,
} from '../../api/inventory';
import { COUPON_VALIDATION_ERRORS, validateCoupon } from '../../api/promotions';
import {
  amendPosSale, createPosSale, getPosSale, instantOnboardPos, listPosSales, mapPosAmendError,
  mapPosChargeError, mapPosInstantOnboardError, normalizePosSalePayload,
  POS_CHARGE_ERRORS, posSalesDateIst, previewPosAmend,
} from '../../api/pos';
import { allocatePosCustomerNumber } from '../../utils/posCustomerNumber';
import { loadPosCounter, savePosCounter } from '../../utils/posCounter';
import { POS_COUNTER, POS_GSTIN } from '../../utils/escpos';
import { canAmendPos } from '../../auth/routing';
import { cartLineKey, resolveLotForCart } from '../../utils/lots';
import {
  calculateBillSavings,
  formatDiscountLabel,
  formatDiscountPercent,
  formatItemSavingsText,
  getItemSavings,
  payableForTender,
  phoneDigits,
} from '../../utils/posMoney';
import {
  SOLD_BY, cartQtyFooter, formatPrice, formatQty, formatStock, formatWeightKg, isSoldByWeight, lineAmount, quantizeKg,
} from '../../utils/soldBy';
import { ThermalPrinterProvider } from '../../context/ThermalPrinterContext';
import Receipt from './Receipt';
import LotPickerModal from './LotPickerModal';
import WeighSheetModal from './WeighSheetModal';
import PosHistoryModal from './PosHistoryModal';
import PosAmendSettlementModal from './PosAmendSettlementModal';
import InstantOnboardModal from './InstantOnboardModal';
import styles from './POS.module.css';

const PAYMENT_METHODS = [
  { key: 'CASH', label: 'Cash', icon: Banknote },
  { key: 'UPI', label: 'UPI', icon: Wallet },
  { key: 'CARD', label: 'Card', icon: CreditCard },
];

const POS_INSTANT_ONBOARD_ENABLED = process.env.NODE_ENV === 'development'
  || process.env.REACT_APP_POS_INSTANT_ONBOARD_ENABLED === 'true';

function buildCartFromSale(sale, products) {
  const lineItems = Array.isArray(sale?.line_items) ? sale.line_items : [];
  const credits = {};
  const cart = [];

  lineItems.forEach((li, index) => {
    const itemId = li.item_id || li.id;
    if (!itemId) return;
    const lotId = li.lot_id || li.lotId || null;
    const product = products.find((p) => p.id === itemId) || {};
    const lineSoldBy = String(li.sold_by || li.soldBy || product.soldBy || '').toUpperCase() === 'WEIGHT'
      ? SOLD_BY.WEIGHT
      : SOLD_BY.EACH;
    const qtyRaw = Number(li.qty);
    if (!Number.isFinite(qtyRaw) || qtyRaw <= 0) return;
    const qty = lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(qtyRaw) : qtyRaw;
    const lineKey = cartLineKey(itemId, lotId);
    credits[lineKey] = (credits[lineKey] || 0) + qty;
    const catalogStock = Number(product.stock) || 0;
    cart.push({
      lineKey,
      id: itemId,
      lotId,
      requiresLot: Boolean(lotId),
      name: li.name || product.name || itemId,
      sku: li.sku || product.sku || '',
      price: Number(li.unit_price ?? li.price ?? product.price) || 0,
      mrp: li.mrp ?? product.mrp,
      unit: li.unit || product.unit || (lineSoldBy === SOLD_BY.WEIGHT ? 'kg' : 'pcs'),
      soldBy: lineSoldBy,
      qtyStep: product.qtyStep ?? (lineSoldBy === SOLD_BY.WEIGHT ? 0.001 : 1),
      minOrderQty: product.minOrderQty ?? (lineSoldBy === SOLD_BY.WEIGHT ? 0.001 : 1),
      maxOrderQty: product.maxOrderQty ?? 10,
      emoji: product.emoji || '🛒',
      swatch: product.swatch || '#c4b59a',
      stock: catalogStock,
      gstRate: product.gstRate || li.gst_rate || 0,
      qty,
      _hydrateIndex: index,
    });
  });

  return { cart, credits };
}

/** Prefer receipt for print; merge sale fields for revision / ids. */
function buildConfirmedAmendReceipt(sale, receipt, fallback = {}) {
  const print = receipt && typeof receipt === 'object' ? receipt : {};
  const meta = sale && typeof sale === 'object' ? sale : {};
  const revision = print.receipt_revision ?? meta.receipt_revision ?? fallback.receipt_revision;
  return {
    ...meta,
    ...print,
    order_id: print.order_id || meta.order_id || fallback.order_id || null,
    display_id: print.display_id || meta.display_id || fallback.display_id || null,
    line_items: print.line_items || meta.line_items || [],
    total: print.total ?? meta.total,
    subtotal: print.subtotal ?? meta.subtotal,
    discount_amount: print.discount_amount ?? meta.discount_amount,
    discount_percent: print.discount_percent ?? meta.discount_percent ?? fallback.discount_percent,
    payment_method: print.payment_method || meta.payment_method,
    coupon_code: print.coupon_code ?? meta.coupon_code ?? null,
    gst_breakup: print.gst_breakup || meta.gst_breakup || [],
    round_off: print.round_off ?? meta.round_off ?? 0,
    customer_phone: print.customer_phone ?? meta.customer_phone ?? null,
    created_at: print.created_at || meta.created_at || meta.updated_at || new Date().toISOString(),
    receipt_revision: revision != null ? Number(revision) : 2,
    revised: print.revised === true || meta.revised === true || Number(revision) > 1,
    note: print.note || meta.note || '(Inclusive of GST)',
  };
}

function salePayloadFromGetResponse(data, orderId) {
  return normalizePosSalePayload(data, { order_id: orderId });
}

export default function POS() {
  return (
    <ThermalPrinterProvider>
      <POSPage />
    </ThermalPrinterProvider>
  );
}

function POSPage() {
  const { products, reloadProducts, mergeProducts } = useProducts();
  const { user } = useAuth();
  const { notify } = useOrders();
  const canEditPosBills = canAmendPos(user?.groups);

  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [captureFocused, setCaptureFocused] = useState(true);
  const storedCounter = loadPosCounter(user?.sub);
  const [counterNumber, setCounterNumber] = useState(storedCounter);
  const [counterSetupOpen, setCounterSetupOpen] = useState(!storedCounter);
  const [counterSetupError, setCounterSetupError] = useState('');
  const [instantOnboard, setInstantOnboard] = useState(null); // { mode: EACH|WEIGHT, barcode?: string }
  const [cart, setCart] = useState([]);
  const [flashId, setFlashId] = useState(null);
  const [cartSearch, setCartSearch] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const [selectedMethods, setSelectedMethods] = useState([]);
  const [paymentAmounts, setPaymentAmounts] = useState({});

  const [receipt, setReceipt] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [reprintOpen, setReprintOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editSale, setEditSale] = useState(null); // { orderId, displayId, couponLocked, couponCode }
  const [amendStockCredit, setAmendStockCredit] = useState({});
  const [amendPreview, setAmendPreview] = useState(null);
  const [amending, setAmending] = useState(false);
  const [repreviewing, setRepreviewing] = useState(false);
  const [amendError, setAmendError] = useState('');
  const [changeDue, setChangeDue] = useState(0);
  const [chargeError, setChargeError] = useState('');
  const [charging, setCharging] = useState(false);
  const [sessionStats, setSessionStats] = useState({ count: 0, total: 0 });
  const [lotPick, setLotPick] = useState(null); // { item, lots }
  const [weighPick, setWeighPick] = useState(null); // weigh sheet context

  const scanRef = useRef(null);
  const scanQueueRef = useRef([]);
  const processingScanRef = useRef(false);
  const isEditMode = Boolean(editSale?.orderId);

  // Keep the chosen counter across refreshes for this signed-in user.
  useEffect(() => {
    const normalized = counterNumber.trim().toUpperCase();
    if (user?.sub && /^[A-Z0-9]{1,16}$/.test(normalized)) {
      savePosCounter(user.sub, normalized);
    }
  }, [counterNumber, user?.sub]);

  // "Sales today" lives on the server — reload after refresh so the header stays accurate.
  const refreshSessionStats = useCallback(async () => {
    if (!user?.idToken) return;
    try {
      const data = await listPosSales({
        date: posSalesDateIst(),
        scope: 'mine',
        limit: 1,
      }, user.idToken);
      const summary = data?.summary;
      if (!summary || typeof summary !== 'object') return;
      setSessionStats({
        count: Number(summary.bill_count) || 0,
        total: Number(summary.gross_total) || 0,
      });
    } catch {
      // Leave current totals; charging will still update the header for this visit.
    }
  }, [user?.idToken]);

  useEffect(() => {
    refreshSessionStats();
  }, [refreshSessionStats]);

  // An unfinished sale survives an inactivity sign-out and comes back when the
  // same person signs in again — nothing is re-keyed at the counter.
  // Skip while amending an existing bill so we don't restore a half-edit later.
  useSessionDraft(
    'pos.sale',
    !isEditMode && cart.length
      ? {
        cart, couponInput, appliedCoupon, selectedMethods, paymentAmounts, counterNumber, customerPhone,
      }
      : undefined,
    (saved) => {
      if (isEditMode) return;
      if (!Array.isArray(saved?.cart) || !saved.cart.length) return;
      setCart(saved.cart);
      undoStackRef.current = [];
      redoStackRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      if (saved.couponInput) setCouponInput(saved.couponInput);
      if (saved.appliedCoupon) setAppliedCoupon(saved.appliedCoupon);
      if (Array.isArray(saved.selectedMethods)) setSelectedMethods(saved.selectedMethods);
      if (saved.paymentAmounts) setPaymentAmounts(saved.paymentAmounts);
      if (saved.customerPhone) setCustomerPhone(String(saved.customerPhone).replace(/\D/g, '').slice(0, 10));
      if (saved.counterNumber) {
        setCounterNumber(saved.counterNumber);
        setCounterSetupOpen(false);
      }
      notify('Restored the sale you had in progress.');
    },
  );

  const focusScanField = () => {
    scanRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!receipt && !reprintOpen && !historyOpen && !amendPreview && !lotPick && !weighPick && !instantOnboard && !counterSetupOpen) {
      focusScanField();
    }
  }, [receipt, reprintOpen, historyOpen, amendPreview, lotPick, weighPick, instantOnboard, counterSetupOpen]);

  const activeProducts = useMemo(() => products.filter((p) => p.status === 'ACTIVE'), [products]);

  const query = scanValue.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!query) return [];
    return activeProducts.filter((p) => (
      p.name.toLowerCase().includes(query)
      || p.sku.toLowerCase().includes(query)
      || (p.barcode || '').includes(query)
    ));
  }, [query, activeProducts]);

  const openInstantOnboard = (mode, barcode = '') => {
    setInstantOnboard({ mode, barcode });
    setScanError('');
  };

  const cartQtyByLineKey = useMemo(
    () => Object.fromEntries(cart.map((l) => [l.lineKey, l.qty])),
    [cart],
  );

  const cartQtyByItemId = useMemo(() => {
    const map = {};
    cart.forEach((l) => {
      map[l.id] = (map[l.id] || 0) + l.qty;
    });
    return map;
  }, [cart]);

  const amendCreditByItemId = useMemo(() => {
    if (!isEditMode) return {};
    const map = {};
    Object.entries(amendStockCredit).forEach(([lineKey, qty]) => {
      const itemId = String(lineKey).split('::')[0];
      map[itemId] = (map[itemId] || 0) + (Number(qty) || 0);
    });
    return map;
  }, [isEditMode, amendStockCredit]);

  const tileCartQty = (itemId) => {
    const inCart = cartQtyByItemId[itemId] || 0;
    if (!isEditMode) return inCart;
    return Math.max(0, inCart - (amendCreditByItemId[itemId] || 0));
  };

  const resolveProduct = (product) => {
    const local = products.find((p) => p.id === product.id);
    return local || product;
  };

  const commitUndoStack = (next) => {
    undoStackRef.current = next;
    setUndoStack(next);
  };

  const commitRedoStack = (next) => {
    redoStackRef.current = next;
    setRedoStack(next);
  };

  const clearEditHistory = () => {
    commitUndoStack([]);
    commitRedoStack([]);
  };

  const pushQtyChange = (entry) => {
    if (entry.fromQty === entry.toQty) return;
    commitUndoStack([...undoStackRef.current, entry]);
    commitRedoStack([]);
  };

  const applyQtyEntry = (entry, direction) => {
    const qty = direction === 'undo' ? entry.fromQty : entry.toQty;
    setQtyDrafts((drafts) => {
      if (drafts[entry.line.lineKey] === undefined) return drafts;
      const next = { ...drafts };
      delete next[entry.line.lineKey];
      return next;
    });
    setCart((prev) => {
      const key = entry.line.lineKey;
      if (qty == null) return prev.filter((l) => l.lineKey !== key);
      if (!prev.some((l) => l.lineKey === key)) return [{ ...entry.line, qty }, ...prev];
      return prev.map((l) => (l.lineKey === key ? { ...l, qty } : l));
    });
  };

  const addToCart = (product, {
    keepValue,
    lotId = null,
    unitPrice,
    lotQty,
    mrp,
    requiresLot = false,
    qty = 1,
    soldBy,
    replaceQty = false,
  } = {}) => {
    const item = resolveProduct(product);
    if (requiresLot && !lotId) {
      setScanError(`Select a lot for ${item.name} before adding to cart`);
      return false;
    }
    const lineSoldBy = soldBy || (isSoldByWeight(item) ? SOLD_BY.WEIGHT : SOLD_BY.EACH);
    const lineKey = cartLineKey(item.id, lotId);
    const price = unitPrice !== undefined && unitPrice !== null ? Number(unitPrice) : Number(item.price) || 0;
    const stockCap = lotQty !== undefined && lotQty !== null ? Number(lotQty) : Number(item.stock) || 0;
    const canGapFill = lineSoldBy === SOLD_BY.WEIGHT || item.catalogComplete === false;
    const addQty = Number(qty);
    if (!Number.isFinite(addQty) || addQty <= 0) {
      setScanError('Enter a valid quantity.');
      return false;
    }
    const nextQtyRaw = replaceQty ? addQty : (cartQtyByLineKey[lineKey] || 0) + addQty;
    const nextQty = lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(nextQtyRaw) : nextQtyRaw;
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    if (!canGapFill && nextQty > stockCap + stockCredit) {
      setScanError(
        lineSoldBy === SOLD_BY.WEIGHT
          ? `Only ${formatWeightKg(stockCap + stockCredit)} of ${item.name} left`
          : `${item.name} is out of stock${lotId ? ' on this lot' : ''}`,
      );
      return false;
    }
    const existingLine = cart.find((l) => l.lineKey === lineKey);
    const toQty = existingLine
      ? nextQty
      : (lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(addQty) : addQty);
    const fromQty = existingLine ? existingLine.qty : null;
    const historyLine = existingLine
      ? {
        ...existingLine,
        qty: toQty,
        price,
        stock: stockCap,
        soldBy: lineSoldBy,
        qtyStep: item.qtyStep,
        minOrderQty: item.minOrderQty,
        maxOrderQty: item.maxOrderQty,
        catalogComplete: item.catalogComplete,
        onboardSource: item.onboardSource,
      }
      : {
        lineKey,
        id: item.id,
        lotId: lotId || null,
        requiresLot: Boolean(requiresLot || lotId),
        name: item.name,
        sku: item.sku,
        price,
        mrp: mrp ?? item.mrp,
        unit: item.unit,
        soldBy: lineSoldBy,
        qtyStep: item.qtyStep,
        minOrderQty: item.minOrderQty,
        maxOrderQty: item.maxOrderQty,
        emoji: item.emoji,
        swatch: item.swatch,
        stock: stockCap,
        gstRate: item.gstRate || 0,
        catalogComplete: item.catalogComplete,
        onboardSource: item.onboardSource,
        qty: toQty,
      };
    setCart((prev) => {
      const existing = prev.find((l) => l.lineKey === lineKey);
      if (existing) {
        return prev.map((l) => (l.lineKey === lineKey ? { ...historyLine, qty: toQty } : l));
      }
      return [{ ...historyLine, qty: toQty }, ...prev];
    });
    pushQtyChange({ line: { ...historyLine, qty: toQty }, fromQty, toQty });
    setScanError('');
    setFlashId(lineKey);
    setTimeout(() => setFlashId((id) => (id === lineKey ? null : id)), 900);
    if (!keepValue) setScanValue('');
    focusScanField();
    return true;
  };

  const openWeighSheet = ({
    item, unitPrice, lotQty, mrp, requiresLot = false, lotId = null, keepValue = false, mode = 'add', initialWeight = '',
  }) => {
    setWeighPick({
      item,
      unitPrice: unitPrice !== undefined && unitPrice !== null ? Number(unitPrice) : Number(item.price) || 0,
      stockCap: lotQty !== undefined && lotQty !== null ? Number(lotQty) : Number(item.stock) || 0,
      mrp,
      requiresLot,
      lotId,
      keepValue,
      mode,
      initialWeight,
      qtyStep: item.qtyStep,
      minOrderQty: item.minOrderQty,
      maxOrderQty: item.maxOrderQty,
      lineKey: mode === 'edit' ? cartLineKey(item.id, lotId) : null,
    });
  };

  const applyResolvedLot = (resolved, { keepValue } = {}) => {
    if (resolved.action === 'pick_lot') {
      setLotPick({ item: resolved.item, lots: resolved.lots });
      return;
    }
    if (resolved.requiresLot && !resolved.lotId) {
      setScanError(`${resolved.item.name} has open lots — pick a pack price`);
      return;
    }
    if (isSoldByWeight(resolved.item)) {
      openWeighSheet({
        item: resolved.item,
        unitPrice: resolved.unitPrice,
        lotQty: resolved.lotQty,
        mrp: resolved.mrp,
        requiresLot: resolved.requiresLot,
        lotId: resolved.lotId,
        keepValue,
        mode: 'add',
      });
      return;
    }
    addToCart(resolved.item, {
      keepValue,
      lotId: resolved.lotId,
      unitPrice: resolved.unitPrice,
      lotQty: resolved.lotQty,
      mrp: resolved.mrp,
      requiresLot: resolved.requiresLot,
    });
  };

  const resolveProductWithLots = async (product) => {
    const item = resolveProduct(product);
    try {
      const { lots } = await listLots(item.id, user.idToken);
      return resolveLotForCart({
        item,
        lots,
        lotPickRequired: false, // derived from available lots' distinct prices
        hasLots: true,
      });
    } catch (err) {
      if (err.status === 404 || err.message === 'not_found') {
        return resolveLotForCart({ item, lots: null, lotPickRequired: false, hasLots: false });
      }
      throw err;
    }
  };

  const addExistingInstantProduct = async (product, qty, lookupData) => {
    const resolved = lookupData
      ? resolveLotForCart(lookupData)
      : await resolveProductWithLots(product);
    if (resolved.action === 'pick_lot') {
      setLotPick({
        item: resolved.item,
        lots: resolved.lots,
        qty,
        instantEntry: true,
      });
      setInstantOnboard(null);
      return;
    }
    const added = addToCart(resolved.item, {
      lotId: resolved.lotId,
      unitPrice: resolved.unitPrice,
      lotQty: resolved.lotQty,
      mrp: resolved.mrp,
      requiresLot: resolved.requiresLot,
      qty,
      soldBy: resolved.item.soldBy,
    });
    if (!added) throw new Error('This product cannot be added with the entered quantity.');
    setInstantOnboard(null);
  };

  const handleInstantOnboard = async (payload) => {
    try {
      const result = await instantOnboardPos(payload, user.idToken);
      mergeProducts([result.item]);
      const lot = result.lots.find((entry) => entry.lotId === result.lotId) || result.lots[0];
      const added = addToCart(result.item, {
        lotId: result.lotId,
        unitPrice: lot?.sellingPrice ?? result.item.price,
        lotQty: Math.max(Number(lot?.qty) || 0, Number(payload.qty) || 0),
        mrp: lot?.mrp ?? result.item.mrp,
        requiresLot: Boolean(result.lotId),
        qty: payload.qty,
        soldBy: payload.sold_by,
      });
      if (!added) throw new Error('The new item could not be added to the cart.');
      setInstantOnboard(null);
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'barcode_taken' && payload.barcode) {
        const existing = await lookupByBarcode(payload.barcode, user.idToken);
        mergeProducts([existing.item]);
        await addExistingInstantProduct(existing.item, Number(payload.qty), existing);
        return;
      }
      if (code === 'name_taken' && err?.body?.item_id) {
        const existing = await getProduct(err.body.item_id, user.idToken);
        mergeProducts([existing]);
        await addExistingInstantProduct(existing, Number(payload.qty));
        return;
      }
      if (code === 'name_taken') {
        const { items } = await listProducts(user.idToken, {
          q: payload.name,
          limit: 50,
          includeInactive: true,
        });
        const normalizedName = String(payload.name || '').trim().toLowerCase();
        const existing = items.find((item) => (
          item.name.trim().toLowerCase() === normalizedName
          && item.status === 'ACTIVE'
          && (payload.sold_by === 'WEIGHT' ? item.soldBy === 'WEIGHT' : item.soldBy !== 'WEIGHT')
        ));
        if (existing) {
          mergeProducts([existing]);
          await addExistingInstantProduct(existing, Number(payload.qty));
          return;
        }
      }
      throw new Error(mapPosInstantOnboardError(err));
    }
  };

  const resolveScannedCode = async (code) => {
    try {
      const data = await lookupByBarcode(code, user.idToken);
      if (data.item.status !== 'ACTIVE') {
        setScanError(`${data.item.name} is not available for sale`);
        return;
      }
      applyResolvedLot(resolveLotForCart(data), { keepValue: true });
      return;
    } catch (err) {
      if (err.message !== 'not_found') {
        setScanError(err.message || 'Could not look up barcode.');
        return;
      }
    }

    const byBarcode = activeProducts.find((p) => p.barcode === code);
    if (byBarcode) {
      try {
        applyResolvedLot(await resolveProductWithLots(byBarcode), { keepValue: true });
      } catch (err) {
        setScanError(err.message || 'Could not load lots.');
      }
      return;
    }

    const matches = activeProducts.filter((p) => (
      p.name.toLowerCase().includes(code.toLowerCase())
      || p.sku.toLowerCase().includes(code.toLowerCase())
      || (p.barcode || '').includes(code)
    ));
    if (matches.length === 1) {
      try {
        applyResolvedLot(await resolveProductWithLots(matches[0]), { keepValue: true });
      } catch (err) {
        setScanError(err.message || 'Could not load lots.');
      }
      return;
    }
    if (matches.length > 1) { setScanError('Multiple matches — pick one below'); return; }
    if (POS_INSTANT_ONBOARD_ENABLED) {
      openInstantOnboard('EACH', code);
    } else {
      setScanError(`No product found for "${code}". Instant onboarding is not enabled yet.`);
    }
  };

  const handleQuickAdd = async (product, { keepValue } = {}) => {
    try {
      applyResolvedLot(await resolveProductWithLots(product), { keepValue });
    } catch (err) {
      setScanError(err.message || 'Could not load lots.');
    }
  };

  const drainScanQueue = async () => {
    if (processingScanRef.current) return;
    processingScanRef.current = true;
    setScanning(true);
    setScanError('');
    try {
      while (scanQueueRef.current.length > 0) {
        const code = scanQueueRef.current.shift();
        await resolveScannedCode(code);
      }
    } finally {
      processingScanRef.current = false;
      setScanning(false);
      setScanValue('');
      requestAnimationFrame(() => focusScanField());
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const code = scanValue.trim();
    if (!code) return;
    e.preventDefault();
    setScanValue('');
    scanQueueRef.current.push(code);
    drainScanQueue();
  };

  const changeQty = (lineKey, delta) => {
    setQtyDrafts((prev) => {
      if (prev[lineKey] === undefined) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
    const line = cart.find((l) => l.lineKey === lineKey);
    if (!line || line.soldBy === SOLD_BY.WEIGHT) return;
    const nextQty = line.qty + delta;
    if (nextQty <= 0) {
      pushQtyChange({ line, fromQty: line.qty, toQty: null });
      setCart((prev) => prev.filter((l) => l.lineKey !== lineKey));
      return;
    }
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    if (line.catalogComplete !== false && nextQty > line.stock + stockCredit) return;
    pushQtyChange({ line: { ...line, qty: nextQty }, fromQty: line.qty, toQty: nextQty });
    setCart((prev) => prev.map((l) => (l.lineKey === lineKey ? { ...l, qty: nextQty } : l)));
  };

  const setQty = (lineKey, rawQty) => {
    const line = cart.find((l) => l.lineKey === lineKey);
    if (!line || line.soldBy === SOLD_BY.WEIGHT) return;
    const parsed = Math.floor(Number(rawQty));
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    const max = line.catalogComplete !== false ? line.stock + stockCredit : Number.POSITIVE_INFINITY;
    const nextQty = Math.min(parsed, max);
    if (nextQty <= 0 || nextQty === line.qty) return;
    pushQtyChange({ line: { ...line, qty: nextQty }, fromQty: line.qty, toQty: nextQty });
    setCart((prev) => prev.map((l) => (l.lineKey === lineKey ? { ...l, qty: nextQty } : l)));
  };

  const editWeightLine = (line) => {
    openWeighSheet({
      item: {
        id: line.id,
        name: line.name,
        price: line.price,
        unit: line.unit,
        stock: line.stock,
        soldBy: SOLD_BY.WEIGHT,
        qtyStep: line.qtyStep,
        minOrderQty: line.minOrderQty,
        maxOrderQty: line.maxOrderQty,
        emoji: line.emoji,
        swatch: line.swatch,
      },
      unitPrice: line.price,
      lotQty: line.stock,
      mrp: line.mrp,
      requiresLot: line.requiresLot,
      lotId: line.lotId,
      mode: 'edit',
      initialWeight: line.qty,
    });
  };

  const removeLine = (lineKey) => {
    const line = cart.find((l) => l.lineKey === lineKey);
    if (line) pushQtyChange({ line, fromQty: line.qty, toQty: null });
    setCart((prev) => prev.filter((l) => l.lineKey !== lineKey));
    setQtyDrafts((prev) => {
      if (prev[lineKey] === undefined) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
  };

  const undoLastAdd = () => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    applyQtyEntry(entry, 'undo');
    commitUndoStack(stack.slice(0, -1));
    commitRedoStack([...redoStackRef.current, entry]);
  };

  const redoLastUndo = () => {
    const stack = redoStackRef.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    applyQtyEntry(entry, 'redo');
    commitRedoStack(stack.slice(0, -1));
    commitUndoStack([...undoStackRef.current, entry]);
  };

  const clearCart = () => {
    setCart([]);
    setCartSearch('');
    setCustomerPhone('');
    clearEditHistory();
    setQtyDrafts({});
    setLotPick(null);
    setWeighPick(null);
    if (isEditMode) {
      setChargeError(POS_CHARGE_ERRORS.items_required);
    }
  };

  const cartQuery = cartSearch.trim().toLowerCase();
  const visibleCartLines = useMemo(() => {
    if (!cartQuery) return cart;
    return cart.filter((l) => (
      l.name.toLowerCase().includes(cartQuery) || (l.sku || '').toLowerCase().includes(cartQuery)
    ));
  }, [cart, cartQuery]);

  const subtotal = useMemo(() => cart.reduce((sum, l) => sum + lineAmount(l.price, l.qty), 0), [cart]);

  const discount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  /** Merchandise total (subtotal − discount) — unrounded; lines stay at paise. */
  const merchandiseTotal = Math.max(0, subtotal - discount);
  const total = merchandiseTotal;

  const prevSubtotalRef = useRef(subtotal);
  useEffect(() => {
    if (editSale?.couponLocked) {
      prevSubtotalRef.current = subtotal;
      return;
    }
    if (appliedCoupon && prevSubtotalRef.current !== subtotal) {
      setAppliedCoupon(null);
      setCouponError('Cart changed — re-apply the coupon to confirm the discount.');
    }
    prevSubtotalRef.current = subtotal;
  }, [subtotal, appliedCoupon, editSale?.couponLocked]);

  const isSplit = selectedMethods.length > 1;
  const singleMethod = selectedMethods.length === 1 ? selectedMethods[0] : null;
  /** CASH preview: nearest ₹1 half-up. UPI/CARD: exact. Split: no FE cash round. */
  const tenderForRound = !isEditMode && !isSplit && singleMethod ? singleMethod : null;
  const { payable: payableTotal, round_off: previewRoundOff } = useMemo(
    () => payableForTender(merchandiseTotal, tenderForRound || 'UPI'),
    [merchandiseTotal, tenderForRound],
  );
  const cashAmount = Number(paymentAmounts.CASH) || 0;
  const paymentTotal = useMemo(
    () => selectedMethods.reduce((sum, m) => sum + (Number(paymentAmounts[m]) || 0), 0),
    [selectedMethods, paymentAmounts],
  );
  const chargeDue = (!isEditMode && !isSplit && singleMethod === 'CASH') ? payableTotal : total;
  const balanceDue = isSplit ? Math.max(0, total - paymentTotal) : 0;
  const computedChangeDue = isSplit
    ? (paymentTotal >= total ? paymentTotal - total : 0)
    : (singleMethod === 'CASH' ? Math.max(0, cashAmount - chargeDue) : 0);
  const missingLotLine = cart.find((line) => line.requiresLot && !line.lotId);
  const normalizedCounter = counterNumber.trim().toUpperCase();
  const counterIsValid = /^[A-Z0-9]{1,16}$/.test(normalizedCounter);
  const canCharge = !isEditMode && counterIsValid && cart.length > 0 && !charging && (
    isSplit
      ? selectedMethods.length >= 2 && paymentTotal >= total
      : Boolean(singleMethod) && (singleMethod !== 'CASH' || cashAmount >= chargeDue)
  );
  const canPreviewAmend = isEditMode
    && cart.length > 0
    && !charging
    && !amending
    && !missingLotLine
    && selectedMethods.length === 1;

  const confirmCounterSetup = (event) => {
    event.preventDefault();
    if (!normalizedCounter) {
      setCounterSetupError(POS_CHARGE_ERRORS.counter_required);
      return;
    }
    if (!counterIsValid) {
      setCounterSetupError(POS_CHARGE_ERRORS.invalid_counter);
      return;
    }
    setCounterNumber(normalizedCounter);
    setCounterSetupError('');
    setCounterSetupOpen(false);
    requestAnimationFrame(() => focusScanField());
  };

  const togglePaymentMethod = (key) => {
    setChargeError('');
    // Bill edit: single tender only (amend API takes one payment_method).
    if (isEditMode) {
      setSelectedMethods([key]);
      return;
    }
    if (selectedMethods.includes(key)) {
      setSelectedMethods((prev) => prev.filter((m) => m !== key));
      setPaymentAmounts((amounts) => {
        const next = { ...amounts };
        delete next[key];
        return next;
      });
      return;
    }
    setSelectedMethods((prev) => [...prev, key]);
  };

  const setPaymentAmount = (method, value) => {
    setPaymentAmounts((prev) => ({ ...prev, [method]: value }));
  };

  const fillRemainingAmount = (method) => {
    const otherTotal = selectedMethods
      .filter((m) => m !== method)
      .reduce((sum, m) => sum + (Number(paymentAmounts[m]) || 0), 0);
    const remaining = Math.max(0, total - otherTotal);
    setPaymentAmount(method, remaining ? String(remaining) : '');
  };

  const resolvePaymentMethod = () => {
    if (!isSplit) return singleMethod;
    const nonCash = selectedMethods.find((m) => m !== 'CASH');
    if (nonCash) return nonCash;
    return selectedMethods.reduce((best, m) => (
      (Number(paymentAmounts[m]) || 0) > (Number(paymentAmounts[best]) || 0) ? m : best
    ), selectedMethods[0]);
  };

  const applyCoupon = async () => {
    if (editSale?.couponLocked) {
      setCouponError(POS_CHARGE_ERRORS.coupon_locked);
      return;
    }
    const code = couponInput.trim().toUpperCase();
    if (!code || couponChecking) return;
    setCouponChecking(true);
    setCouponError('');
    try {
      const result = await validateCoupon({ couponCode: code, subtotal }, user.idToken);
      setAppliedCoupon({
        code: result.couponCode || code,
        discountAmount: result.discountAmount,
        discountPercent: result.discountPercent,
      });
      setCouponInput('');
    } catch (err) {
      setCouponError(COUPON_VALIDATION_ERRORS[err.message] || err.message || 'Could not validate this coupon.');
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    if (editSale?.couponLocked) {
      setCouponError(POS_CHARGE_ERRORS.coupon_locked);
      return;
    }
    setAppliedCoupon(null);
    setCouponError('');
  };

  const resetCheckout = () => {
    setChangeDue(0);
    setCart([]);
    clearEditHistory();
    setQtyDrafts({});
    setCustomerPhone('');
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
    setSelectedMethods([]);
    setPaymentAmounts({});
    setChargeError('');
    setCartSearch('');
    setAmendPreview(null);
    setAmendError('');
    setAmendStockCredit({});
    setEditSale(null);
    setRepreviewing(false);
  };

  const exitEditMode = () => {
    resetCheckout();
    setScanValue('');
    setScanError('');
    setLotPick(null);
    setWeighPick(null);
    focusScanField();
  };

  const beginEditSale = (salePayload) => {
    if (!canEditPosBills) {
      notify('You do not have permission to edit POS bills.');
      return;
    }
    if (salePayload?.voided) {
      notify('This bill is already voided — it cannot be amended.');
      return;
    }
    const orderId = salePayload?.order_id;
    if (!orderId) {
      notify('Could not open this bill for editing.');
      return;
    }
    const { cart: nextCart, credits } = buildCartFromSale(salePayload, products);
    if (!nextCart.length) {
      notify('This bill has no line items to edit.');
      return;
    }
    const couponCode = salePayload.coupon_code || null;
    const originalTender = String(salePayload.payment_method || '').toUpperCase();
    const knownTender = ['CASH', 'UPI', 'CARD'].includes(originalTender) ? originalTender : null;
    setEditSale({
      orderId,
      displayId: salePayload.display_id || orderId,
      couponLocked: Boolean(couponCode),
      couponCode,
      originalTotal: salePayload.total,
      originalPaymentMethod: knownTender,
      originalPhone: phoneDigits(
        salePayload.customer_phone
        || salePayload.customerPhone
        || salePayload.phone
        || '',
      ),
    });
    setAmendStockCredit(credits);
    setCart(nextCart);
    clearEditHistory();
    setQtyDrafts({});
    setCustomerPhone(phoneDigits(
      salePayload.customer_phone
      || salePayload.customerPhone
      || salePayload.phone
      || '',
    ));
    setAppliedCoupon(couponCode
      ? {
        code: couponCode,
        discountAmount: Number(salePayload.discount_amount) || 0,
        discountPercent: salePayload.discount_percent != null ? Number(salePayload.discount_percent) : undefined,
      }
      : null);
    setCouponInput('');
    setCouponError(couponCode ? 'Original coupon is locked on this bill.' : '');
    setSelectedMethods(knownTender ? [knownTender] : []);
    setPaymentAmounts({});
    setChargeError('');
    setCartSearch('');
    setAmendPreview(null);
    setAmendError('');
    setScanValue('');
    setScanError('');
    focusScanField();
  };

  const cartItemsPayload = () => cart.map((line) => {
    const qty = line.soldBy === SOLD_BY.WEIGHT ? quantizeKg(line.qty) : line.qty;
    const item = { item_id: line.id, qty };
    if (line.lotId) item.lot_id = line.lotId;
    return item;
  });

  const closeReceipt = () => {
    setReceipt(null);
    setChangeDue(0);
    setScanValue('');
    setScanError('');
    focusScanField();
  };

  const handleCharge = async () => {
    if (!normalizedCounter) {
      setChargeError(POS_CHARGE_ERRORS.counter_required);
      return;
    }
    if (!counterIsValid) {
      setChargeError(POS_CHARGE_ERRORS.invalid_counter);
      return;
    }
    if (!canCharge) return;
    if (missingLotLine) {
      setChargeError(POS_CHARGE_ERRORS.lot_id_required);
      return;
    }
    setCharging(true);
    setChargeError('');
    try {
      if (customerPhone && customerPhone.length !== 10) {
        setChargeError(POS_CHARGE_ERRORS.invalid_phone);
        setCharging(false);
        return;
      }
      const change = computedChangeDue;
      const { receipt: saleReceipt } = await createPosSale({
        items: cartItemsPayload(),
        payment_method: resolvePaymentMethod(),
        counter: normalizedCounter,
        coupon_code: appliedCoupon?.code,
        discount_percent: 0,
        customer_phone: customerPhone || undefined,
      }, user.idToken);

      const cartByItemId = new Map(cart.map((c) => [c.id || c.item_id, c]));
      const enrichedLineItems = (saleReceipt.line_items || []).map((li) => {
        const cartItem = cartByItemId.get(li.item_id || li.id);
        const mrp = Number(li.mrp ?? cartItem?.mrp);
        return {
          ...li,
          mrp: Number.isFinite(mrp) && mrp > 0 ? mrp : undefined,
        };
      });

      const confirmed = {
        ...saleReceipt,
        line_items: enrichedLineItems.length > 0 ? enrichedLineItems : (saleReceipt.line_items || []),
        counter: saleReceipt.counter || normalizedCounter,
        gstin: saleReceipt.gstin || POS_GSTIN,
        cashier_name: saleReceipt.cashier_name || user.name,
        customer_number: saleReceipt.customer_number || allocatePosCustomerNumber(),
        customer_phone: saleReceipt.customer_phone
          || (customerPhone ? `+91${customerPhone}` : undefined),
        round_off: saleReceipt.round_off ?? 0,
        discount_amount: saleReceipt.discount_amount ?? saleReceipt.discount ?? discount,
        discount_percent: saleReceipt.discount_percent
          ?? appliedCoupon?.discountPercent
          ?? (subtotal > 0 && discount > 0 ? (discount / subtotal) * 100 : undefined),
        subtotal: saleReceipt.subtotal ?? subtotal,
        coupon_code: saleReceipt.coupon_code ?? appliedCoupon?.code ?? null,
      };
      // Change is FE-local vs server payable total when cash was tendered.
      const payable = Number(saleReceipt.total);
      const changeFromServer = singleMethod === 'CASH' && Number.isFinite(payable) && cashAmount > 0
        ? Math.max(0, cashAmount - payable)
        : change;
      resetCheckout();
      setChangeDue(changeFromServer);
      setReceipt(confirmed);
      setLastReceipt(confirmed);
      setSessionStats((s) => ({
        count: s.count + 1,
        total: s.total + (Number.isFinite(payable) ? payable : chargeDue),
      }));
      reloadProducts();
    } catch (err) {
      setChargeError(mapPosChargeError(err));
    } finally {
      setCharging(false);
    }
  };

  const customerPhoneForAmend = () => {
    const next = phoneDigits(customerPhone);
    const original = phoneDigits(editSale?.originalPhone);
    if (next === original) return undefined; // omit → keep
    if (!next) return ''; // clear
    return next;
  };

  const buildAmendPreviewBody = () => {
    const previewBody = {
      items: cartItemsPayload(),
      discount_percent: 0,
    };
    // Omit coupon_code when original had one — server keeps it.
    if (!editSale?.couponLocked && appliedCoupon?.code) {
      previewBody.coupon_code = appliedCoupon.code;
    }
    const phone = customerPhoneForAmend();
    if (phone !== undefined) previewBody.customer_phone = phone;
    return previewBody;
  };

  const runAmendPreview = async () => {
    const preview = await previewPosAmend(editSale.orderId, buildAmendPreviewBody(), user.idToken);
    if (!preview?.settlement?.direction) {
      throw Object.assign(new Error('Could not load settlement from preview.'), { code: 'invalid_preview' });
    }
    return preview;
  };

  const reloadEditSaleFromServer = async (reasonMessage) => {
    if (!editSale?.orderId) {
      exitEditMode();
      return;
    }
    try {
      const data = await getPosSale(editSale.orderId, user.idToken);
      beginEditSale(salePayloadFromGetResponse(data, editSale.orderId));
      notify(reasonMessage
        ? `${reasonMessage} Bill reloaded — preview again.`
        : 'Bill reloaded — preview again.');
    } catch (reloadErr) {
      notify(mapPosAmendError(reloadErr));
      exitEditMode();
    }
  };

  const handlePreviewAmend = async () => {
    if (!canPreviewAmend) {
      if (!cart.length) {
        const msg = POS_CHARGE_ERRORS.items_required;
        setChargeError(msg);
        notify(msg);
      } else if (missingLotLine) {
        const msg = POS_CHARGE_ERRORS.lot_id_required;
        setChargeError(msg);
        notify(msg);
      }
      return;
    }
    if (customerPhone && customerPhone.length !== 10) {
      setChargeError(POS_CHARGE_ERRORS.invalid_phone);
      notify(POS_CHARGE_ERRORS.invalid_phone);
      return;
    }
    setCharging(true);
    setChargeError('');
    setAmendError('');
    try {
      const preview = await runAmendPreview();
      setAmendPreview(preview);
    } catch (err) {
      const code = err?.code || err?.message;
      const message = mapPosAmendError(err);
      setChargeError(message);
      setAmendPreview(null);
      notify(message);
      if (code === 'amend_day_locked' || code === 'not_found') {
        exitEditMode();
      } else if (code === 'amend_conflict') {
        await reloadEditSaleFromServer(message);
      } else if (code === 'item_unavailable' || code === 'lot_mismatch') {
        // Catalog item/lot may be inactive or gone — stay in edit so cashier can remove the line.
        setChargeError(message);
      }
    } finally {
      setCharging(false);
    }
  };

  const handleConfirmAmend = async ({ payment_method } = {}) => {
    if (!editSale?.orderId || !amendPreview || amending || repreviewing) return;

    const settlement = amendPreview.settlement;
    const settlement_direction = String(settlement?.direction || '').toUpperCase();
    // EVEN must send amount 0 (required) — do not omit settlement_amount.
    const settlement_amount = settlement_direction === 'EVEN'
      ? (settlement?.amount ?? 0)
      : settlement?.amount;
    if (!settlement_direction || settlement_amount == null || !Number.isFinite(Number(settlement_amount))) {
      const msg = 'Preview incomplete — run Preview amend again.';
      setAmendError(msg);
      notify(msg);
      return;
    }
    if ((settlement_direction === 'DUE' || settlement_direction === 'REFUND' || settlement_direction === 'EVEN')
      && !payment_method) {
      setAmendError(POS_CHARGE_ERRORS.invalid_payment_method);
      notify(POS_CHARGE_ERRORS.invalid_payment_method);
      return;
    }

    setAmending(true);
    setAmendError('');
    try {
      const amendBody = {
        items: cartItemsPayload(),
        settlement_direction,
        settlement_amount: Number(settlement_amount),
        discount_percent: 0,
      };
      if (payment_method) amendBody.payment_method = payment_method;
      const phone = customerPhoneForAmend();
      if (phone !== undefined) amendBody.customer_phone = phone;

      const data = await amendPosSale(editSale.orderId, amendBody, user.idToken);
      const saleReceipt = data?.receipt || null;
      const sale = data?.sale || null;
      const confirmed = buildConfirmedAmendReceipt(sale, saleReceipt, {
        order_id: editSale.orderId,
        display_id: editSale.displayId,
      });
      confirmed.counter = confirmed.counter || POS_COUNTER;
      confirmed.gstin = confirmed.gstin || POS_GSTIN;
      confirmed.cashier_name = confirmed.cashier_name || user.name;
      confirmed.customer_number = confirmed.customer_number || allocatePosCustomerNumber();

      setAmendPreview(null);
      resetCheckout();
      setHistoryOpen(false);
      setChangeDue(0);
      setReceipt(confirmed);
      setLastReceipt(confirmed);
      await reloadProducts();
      notify(`Bill ${confirmed.display_id || editSale.displayId} revised (r${confirmed.receipt_revision || 2}).`);
    } catch (err) {
      const code = err?.code || err?.message;
      const message = mapPosAmendError(err);
      notify(message);

      if (code === 'amount_mismatch') {
        setAmending(false);
        setRepreviewing(true);
        setAmendError(message);
        try {
          const preview = await runAmendPreview();
          setAmendPreview(preview);
          setAmendError('Totals changed — preview refreshed. Confirm again with the new settlement.');
        } catch (previewErr) {
          const previewMessage = mapPosAmendError(previewErr);
          setAmendPreview(null);
          setChargeError(previewMessage);
          notify(previewMessage);
        } finally {
          setRepreviewing(false);
        }
        return;
      }

      setAmendError(message);
      setChargeError(message);

      if (code === 'amend_conflict') {
        setAmendPreview(null);
        setAmending(false);
        await reloadEditSaleFromServer(message);
        return;
      }
      if (code === 'amend_day_locked' || code === 'not_found') {
        setAmendPreview(null);
        exitEditMode();
        return;
      }
      if (code === 'coupon_locked') {
        // Stay on settlement / edit so cashier can continue without swapping coupon.
        return;
      }
      if (code === 'item_unavailable' || code === 'lot_mismatch' || code === 'insufficient_stock') {
        // Catalog may have changed; close settlement and let cashier fix the cart.
        setAmendPreview(null);
      }
    } finally {
      setAmending(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Point of Sale"
        title={isEditMode ? `Edit bill ${editSale.displayId}` : 'New Sale'}
        subtitle={isEditMode
          ? 'Change items, then preview settlement. Stock and totals come from the server.'
          : cart.some((l) => l.soldBy === SOLD_BY.WEIGHT)
            ? 'Repeat scan adds weight.'
            : 'Scan or search packed goods; tap loose items to weigh.'}
        actions={(
          <div className={styles.headerActions}>
            <div className={styles.headerTopRow}>
              {!isEditMode && (
                <label className={styles.counterField}>
                  <span>Counter number</span>
                  <div className={styles.counterInputWrap}>
                    <input
                      type="text"
                      value={counterNumber}
                      maxLength={16}
                      pattern="[A-Za-z0-9]{1,16}"
                      autoComplete="off"
                      aria-invalid={Boolean(counterNumber) && !counterIsValid}
                      aria-describedby="pos-counter-hint"
                      onChange={(e) => {
                        setCounterNumber(e.target.value.toUpperCase());
                        setCounterSetupError('');
                        if (
                          chargeError === POS_CHARGE_ERRORS.counter_required
                          || chargeError === POS_CHARGE_ERRORS.invalid_counter
                        ) setChargeError('');
                      }}
                    />
                    <small
                      id="pos-counter-hint"
                      className={counterNumber && !counterIsValid ? styles.counterHintError : ''}
                    >
                      1–16 letters or numbers
                    </small>
                  </div>
                </label>
              )}
              {isEditMode ? (
                <Button type="button" variant="outline" size="sm" onClick={exitEditMode}>
                  {historyOpen ? 'Back' : 'Cancel edit'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={History}
                  onClick={() => setHistoryOpen(true)}
                >
                  History
                </Button>
              )}
            </div>
            {sessionStats.count > 0 && (
              <div className={styles.sessionActions}>
                <div className={styles.sessionStat}>
                  <span>{sessionStats.count} sale{sessionStats.count > 1 ? 's' : ''} today</span>
                  <strong>{formatCurrency(sessionStats.total)}</strong>
                </div>
                {lastReceipt && (
                  <button type="button" className={styles.reprintLastBtn} onClick={() => setReprintOpen(true)}>
                    Reprint last sale
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      />

      <div className={styles.grid}>
        <div className={styles.left}>
          <div className={styles.scanHeader}>
            <p className={styles.scanLabel}>Scan field — most scanners type directly into it</p>
            <div className={styles.scanHeaderActions}>
              {POS_INSTANT_ONBOARD_ENABLED && (
                <>
                  <Button type="button" variant="outline" size="sm" icon={Plus} onClick={() => openInstantOnboard('EACH')}>
                    Add packaged
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => openInstantOnboard('WEIGHT')}>
                    Loose / weigh
                  </Button>
                </>
              )}
              <button
                type="button"
                className={`${styles.capturePill} ${captureFocused ? styles.capturingOn : styles.capturePaused}`}
                onClick={focusScanField}
              >
                <span className={styles.captureDot} />
                {captureFocused ? 'Listening for input' : 'Paused — click to resume'}
              </button>
            </div>
          </div>
          <div className={styles.scanBox}>
            <ScanBarcode size={20} className={styles.scanIcon} />
            <input
              ref={scanRef}
              autoFocus
              value={scanValue}
              onChange={(e) => { setScanValue(e.target.value); setScanError(''); }}
              onKeyDown={handleScanKeyDown}
              onFocus={() => setCaptureFocused(true)}
              onBlur={() => setCaptureFocused(false)}
              placeholder="Scan barcode, or type a product name / SKU…"
              className={styles.scanInput}
            />
            {scanValue && (
              <button
                className={styles.clearScan}
                onClick={() => { setScanValue(''); setScanError(''); focusScanField(); }}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
          {scanError && <p className={styles.scanError}><AlertTriangle size={13} /> {scanError}</p>}

          <p className={styles.sectionLabel}>{query ? `Results for "${scanValue.trim()}"` : 'Quick add'}</p>
          <div className={styles.productGrid}>
            {(query ? searchResults : activeProducts).length === 0 ? (
              <EmptyState icon={Search} title="No matches" description="Try a different name, SKU, or barcode." />
            ) : (query ? searchResults : activeProducts).map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                inCartQty={tileCartQty(p.id)}
                onAdd={() => handleQuickAdd(p, { keepValue: !!query })}
              />
            ))}
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.cartPanel}>
            <div className={styles.cartHeader}>
              <h2><ShoppingCart size={16} /> Current sale</h2>
              {(cart.length > 0 || undoStack.length > 0 || redoStack.length > 0) && (
                <div className={styles.cartHeaderActions}>
                  <button
                    type="button"
                    className={styles.undoLast}
                    onClick={undoLastAdd}
                    disabled={!undoStack.length}
                    title="Undo last quantity change"
                  >
                    <Undo2 size={13} /> Undo
                  </button>
                  <button
                    type="button"
                    className={styles.undoLast}
                    onClick={redoLastUndo}
                    disabled={!redoStack.length}
                    title="Redo last quantity change"
                  >
                    <Redo2 size={13} /> Redo
                  </button>
                  {cart.length > 0 && (
                    <button type="button" className={styles.clearAll} onClick={clearCart}>Clear all</button>
                  )}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className={styles.cartSearchBox}>
                <Search size={13} />
                <input
                  placeholder="Search items in this sale…"
                  value={cartSearch}
                  onChange={(e) => setCartSearch(e.target.value)}
                />
                {cartSearch && (
                  <button onClick={() => setCartSearch('')} aria-label="Clear search">
                    <X size={13} />
                  </button>
                )}
              </div>
            )}

            <div className={styles.cartList}>
              {cart.length === 0 ? (
                <EmptyState icon={ShoppingCart} title="Cart is empty" description="Scan or tap a product to begin." />
              ) : visibleCartLines.length === 0 ? (
                <p className={styles.cartNoMatch}>No items match "{cartSearch.trim()}"</p>
              ) : visibleCartLines.map((line) => {
                const serial = cart.findIndex((l) => l.lineKey === line.lineKey) + 1;
                return (
                <div key={line.lineKey} className={`${styles.cartLine} ${flashId === line.lineKey ? styles.flash : ''}`}>
                  <span className={styles.lineSerial}>{serial}</span>
                  <span className={styles.lineEmoji} style={{ background: `${line.swatch}22`, color: line.swatch }}>
                    {line.emoji}
                  </span>
                  <div className={styles.lineInfo}>
                    <p className={styles.lineName} title={line.name}>
                      {line.name}
                      {line.catalogComplete === false && <span className={styles.newItemBadge}>New · Needs details</span>}
                    </p>
                    <p className={styles.linePrice}>
                      {formatPrice(line, formatCurrency)}
                      {line.lotId ? ' · lot' : ''}
                      {(() => {
                        const s = getItemSavings(line, line.mrp);
                        if (s.unitSavings <= 0) return null;
                        const sText = formatItemSavingsText(s, line, formatCurrency);
                        return sText ? (
                          <span className={styles.lineSavings}>
                            {' · '}{sText}
                          </span>
                        ) : null;
                      })()}
                    </p>
                  </div>
                  {line.soldBy === SOLD_BY.WEIGHT ? (
                    <button
                      type="button"
                      className={styles.lineWeight}
                      onClick={() => editWeightLine(line)}
                      aria-label={`Edit weight for ${line.name}`}
                    >
                      {formatQty(line.qty, line)}
                    </button>
                  ) : (
                    <div className={styles.lineQty}>
                      <button onClick={() => changeQty(line.lineKey, -1)} aria-label="Decrease quantity">
                        <Minus size={12} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.lineQtyInput}
                        value={qtyDrafts[line.lineKey] !== undefined ? qtyDrafts[line.lineKey] : String(line.qty)}
                        aria-label={`Quantity for ${line.name}`}
                        onFocus={(e) => {
                          setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: String(line.qty) }));
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^\d]/g, '');
                          if (digits === '') {
                            setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: '' }));
                            return;
                          }
                          const parsed = Math.floor(Number(digits));
                          const stockCredit = isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0;
                          const maxQty = line.catalogComplete !== false
                            ? line.stock + stockCredit
                            : Number.POSITIVE_INFINITY;
                          if (Number.isFinite(maxQty) && parsed > maxQty) {
                            // Do not allow typing above available stock
                            const capped = Math.max(0, Math.floor(maxQty));
                            setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: String(capped) }));
                            if (capped > 0) setQty(line.lineKey, capped);
                            return;
                          }
                          setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: digits }));
                          setQty(line.lineKey, digits);
                        }}
                        onBlur={() => {
                          setQtyDrafts((prev) => {
                            const next = { ...prev };
                            delete next[line.lineKey];
                            return next;
                          });
                          const draft = qtyDrafts[line.lineKey];
                          if (draft === undefined || draft === '' || Number(draft) <= 0) {
                            return;
                          }
                          const stockCredit = isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0;
                          const maxQty = line.catalogComplete !== false
                            ? line.stock + stockCredit
                            : Number.POSITIVE_INFINITY;
                          const parsed = Math.floor(Number(draft));
                          if (Number.isFinite(maxQty) && parsed > maxQty) {
                            if (maxQty > 0) setQty(line.lineKey, maxQty);
                            return;
                          }
                          setQty(line.lineKey, draft);
                        }}
                      />
                      <button
                        onClick={() => changeQty(line.lineKey, 1)}
                        disabled={
                          line.catalogComplete !== false
                          && line.soldBy !== SOLD_BY.WEIGHT
                          && line.qty >= line.stock + (isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0)
                        }
                        aria-label="Increase quantity"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                  <span className={styles.lineTotal}>{formatCurrency(lineAmount(line.price, line.qty))}</span>
                  <button className={styles.lineRemove} onClick={() => removeLine(line.lineKey)} aria-label="Remove item">
                    <Trash2 size={13} />
                  </button>
                </div>
                );
              })}
            </div>

            {cart.length > 0 && (
              <div className={styles.cartCheckout}>
                <div className={styles.customerPhoneBlock}>
                  <label className={styles.customerPhoneField}>
                    <span className={styles.sectionLabel}><Phone size={13} /> Customer phone</span>
                    <div className={styles.customerPhoneInput}>
                      <span>+91</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="10-digit mobile (optional)"
                        value={customerPhone}
                        maxLength={10}
                        onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        aria-label="Customer phone number"
                      />
                    </div>
                  </label>
                </div>

                <div className={styles.discountBlock}>
                  {appliedCoupon ? (
                    <div className={styles.couponApplied}>
                      <span>
                        <Ticket size={13} /> {appliedCoupon.code} applied
                        {editSale?.couponLocked ? ' (locked)' : ''}
                      </span>
                      {!editSale?.couponLocked && (
                        <button onClick={removeCoupon} aria-label="Remove coupon"><X size={13} /></button>
                      )}
                    </div>
                  ) : (
                    <div className={styles.couponRow}>
                      <input
                        placeholder="Coupon code"
                        value={couponInput}
                        disabled={couponChecking || Boolean(editSale?.couponLocked)}
                        onChange={(e) => { setCouponInput(e.target.value); setCouponError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                        style={{ textTransform: 'uppercase' }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={applyCoupon}
                        disabled={!couponInput.trim() || couponChecking || Boolean(editSale?.couponLocked)}
                      >
                        {couponChecking ? 'Checking…' : 'Apply'}
                      </Button>
                    </div>
                  )}
                  {couponError && <p className={styles.couponErrorText}>{couponError}</p>}
                </div>

                <div className={styles.paymentBlock}>
                  <div className={styles.paymentHeadRow}>
                    <p className={styles.sectionLabel}>Payment method</p>
                    {!isEditMode && selectedMethods.length > 0 && (
                      <span className={styles.splitToggleHint}>
                        {isSplit ? 'Split payment' : 'Tap another method to split'}
                      </span>
                    )}
                    {isEditMode && (
                      <span className={styles.splitToggleHint}>
                        {editSale?.originalPaymentMethod
                          && selectedMethods[0]
                          && selectedMethods[0] !== editSale.originalPaymentMethod
                          ? `Was ${editSale.originalPaymentMethod} — updating tender`
                          : 'Change if the customer switched how they pay'}
                      </span>
                    )}
                  </div>
                  <div className={styles.paymentRow}>
                    {PAYMENT_METHODS.map(({ key, label, icon: Icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.paymentBtn} ${selectedMethods.includes(key) ? styles.paymentActive : ''}`}
                        onClick={() => togglePaymentMethod(key)}
                      >
                        <Icon size={16} /> {label}
                      </button>
                    ))}
                  </div>

                  {!isEditMode && singleMethod === 'CASH' && (
                    <div className={styles.tenderedRow}>
                      <div className={styles.tenderedInput}>
                        <span>₹</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Amount received"
                          value={paymentAmounts.CASH || ''}
                          onChange={(e) => setPaymentAmount('CASH', e.target.value)}
                        />
                      </div>
                      {paymentAmounts.CASH !== undefined && paymentAmounts.CASH !== '' && (
                        <span className={cashAmount >= chargeDue ? styles.changeOk : styles.changeShort}>
                          {cashAmount >= chargeDue
                            ? `Change due ${formatCurrency(computedChangeDue)}`
                            : `Short by ${formatCurrency(chargeDue - cashAmount)}`}
                        </span>
                      )}
                    </div>
                  )}

                  {!isEditMode && isSplit && (
                    <div className={styles.splitPayments}>
                      {selectedMethods.map((m) => {
                        const meta = PAYMENT_METHODS.find((pm) => pm.key === m);
                        return (
                          <div key={m} className={styles.splitRow}>
                            <span className={styles.splitLabel}><meta.icon size={13} /> {meta.label}</span>
                            <div className={styles.splitAmountInput}>
                              <span>₹</span>
                              <input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={paymentAmounts[m] || ''}
                                onChange={(e) => setPaymentAmount(m, e.target.value)}
                              />
                            </div>
                            <button type="button" className={styles.fillRemainingBtn} onClick={() => fillRemainingAmount(m)}>
                              Fill
                            </button>
                          </div>
                        );
                      })}
                      <div className={styles.splitSummary}>
                        <span>Collected {formatCurrency(paymentTotal)} of {formatCurrency(total)}</span>
                        {balanceDue > 0 && <span className={styles.changeShort}>Short by {formatCurrency(balanceDue)}</span>}
                        {balanceDue === 0 && computedChangeDue > 0 && (
                          <span className={styles.changeOk}>Change due {formatCurrency(computedChangeDue)}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.totals}>
                  <div className={styles.billRow}><span>Items</span><span>{cartQtyFooter(cart)}</span></div>
                  <div className={styles.billRow}><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                  {discount > 0 && (
                    <div className={`${styles.billRow} ${styles.billDiscount}`}>
                      <span>
                        {formatDiscountLabel({
                          coupon_code: appliedCoupon?.code,
                          discount_amount: discount,
                          subtotal,
                          discount_percent: appliedCoupon?.discountPercent,
                        })}
                      </span>
                      <span>−{formatCurrency(discount)}</span>
                    </div>
                  )}
                  {!isEditMode && previewRoundOff !== 0 && (
                    <div className={styles.billRow}>
                      <span>Round off</span>
                      <span>
                        {previewRoundOff > 0 ? '+' : ''}
                        {formatCurrency(previewRoundOff)}
                      </span>
                    </div>
                  )}
                  <div className={`${styles.billRow} ${styles.billTotal}`}>
                    <span>{isEditMode ? 'Cart total (est.)' : 'To pay'}</span>
                    <span>{formatCurrency(isEditMode ? total : chargeDue)}</span>
                  </div>
                  {(() => {
                    const cartSavings = calculateBillSavings({ line_items: cart, discount_amount: discount, subtotal }, products);
                    if (cartSavings.totalSavings <= 0) return null;
                    return (
                      <div className={`${styles.billRow} ${styles.billSavings}`}>
                        <span>Total Savings</span>
                        <span>
                          {formatCurrency(cartSavings.totalSavings)}
                          {cartSavings.totalSavingsPct > 0 ? ` (${formatDiscountPercent(cartSavings.totalSavingsPct)})` : ''}
                        </span>
                      </div>
                    );
                  })()}
                  {isEditMode && editSale?.originalTotal != null && (
                    <div className={styles.billRow}>
                      <span>Original bill</span>
                      <span>{formatCurrency(editSale.originalTotal)}</span>
                    </div>
                  )}
                  <p className={styles.gstNote}>
                    {isEditMode
                      ? 'Preview to get Collect / Refund / Even from the server before confirming.'
                      : singleMethod === 'CASH' && previewRoundOff !== 0
                        ? 'Cash rounds to the nearest rupee. Final To pay is confirmed on charge.'
                        : 'Prices are inclusive of GST (totals confirmed on charge)'}
                  </p>
                </div>

                {chargeError && <p className={styles.scanError}><AlertTriangle size={13} /> {chargeError}</p>}
                {isEditMode && !cart.length && (
                  <p className={styles.scanError}>
                    <AlertTriangle size={13} /> Empty cart cannot be saved — void the bill from History instead.
                  </p>
                )}

                {isEditMode ? (
                  <Button
                    size="lg"
                    className={styles.chargeBtn}
                    disabled={!canPreviewAmend}
                    onClick={handlePreviewAmend}
                  >
                    {charging
                      ? 'Saving…'
                      : !cart.length
                        ? 'Add items to save'
                        : selectedMethods.length !== 1
                          ? 'Select payment method'
                          : 'Save changes'}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className={styles.chargeBtn}
                    disabled={!canCharge}
                    onClick={handleCharge}
                  >
                    {charging
                      ? 'Charging…'
                      : !normalizedCounter
                        ? 'Enter counter number'
                        : !counterIsValid
                          ? 'Enter a valid counter number'
                      : selectedMethods.length > 0
                        ? `Charge ${formatCurrency(chargeDue)}`
                        : 'Select a payment method'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {POS_INSTANT_ONBOARD_ENABLED && instantOnboard && (
        <InstantOnboardModal
          mode={instantOnboard.mode}
          initialBarcode={instantOnboard.barcode}
          token={user.idToken}
          products={activeProducts}
          onClose={() => setInstantOnboard(null)}
          onCreate={handleInstantOnboard}
          onUseExisting={addExistingInstantProduct}
        />
      )}

      {counterSetupOpen && (
        <div className={styles.counterSetupOverlay} role="dialog" aria-modal="true" aria-labelledby="counter-setup-title">
          <form className={styles.counterSetupModal} onSubmit={confirmCounterSetup}>
            <h2 id="counter-setup-title">Enter counter number</h2>
            <p>Set the counter for this POS session. You can change it later from the POS header.</p>
            <label className={styles.counterSetupField}>
              <span>Counter number</span>
              <input
                type="text"
                value={counterNumber}
                maxLength={16}
                pattern="[A-Za-z0-9]{1,16}"
                autoComplete="off"
                autoFocus
                aria-invalid={Boolean(counterSetupError)}
                onChange={(e) => {
                  setCounterNumber(e.target.value.toUpperCase());
                  setCounterSetupError('');
                }}
              />
              <small>1–16 letters or numbers</small>
            </label>
            {counterSetupError && (
              <p className={styles.counterSetupError} role="alert">
                <AlertTriangle size={14} /> {counterSetupError}
              </p>
            )}
            <Button type="submit">Continue to POS</Button>
          </form>
        </div>
      )}

      {lotPick && (
        <LotPickerModal
          item={lotPick.item}
          lots={lotPick.lots}
          onClose={() => { setLotPick(null); focusScanField(); }}
          onSelect={(lot) => {
            const item = lotPick.item;
            const instantQty = lotPick.instantEntry ? lotPick.qty : null;
            setLotPick(null);
            if (instantQty != null) {
              addToCart(item, {
                keepValue: true,
                lotId: lot.lotId,
                unitPrice: lot.sellingPrice,
                lotQty: lot.qty,
                mrp: lot.mrp,
                requiresLot: true,
                qty: instantQty,
                soldBy: item.soldBy,
              });
              return;
            }
            if (isSoldByWeight(item)) {
              openWeighSheet({
                item,
                unitPrice: lot.sellingPrice,
                lotQty: lot.qty,
                mrp: lot.mrp,
                requiresLot: true,
                lotId: lot.lotId,
                keepValue: true,
                mode: 'add',
              });
              return;
            }
            addToCart(item, {
              keepValue: true,
              lotId: lot.lotId,
              unitPrice: lot.sellingPrice,
              lotQty: lot.qty,
              mrp: lot.mrp,
              requiresLot: true,
            });
          }}
        />
      )}

      {weighPick && (
        <WeighSheetModal
          item={weighPick.item}
          unitPrice={weighPick.unitPrice}
          stockCap={
            isSoldByWeight(weighPick.item)
              ? (Number(weighPick.maxOrderQty) || 1000)
              : weighPick.mode === 'edit'
              ? weighPick.stockCap + (isEditMode ? (Number(amendStockCredit[weighPick.lineKey]) || 0) : 0)
              : Math.max(
                0,
                weighPick.stockCap
                  + (isEditMode ? (Number(amendStockCredit[cartLineKey(weighPick.item.id, weighPick.lotId)]) || 0) : 0)
                  - (cartQtyByLineKey[cartLineKey(weighPick.item.id, weighPick.lotId)] || 0),
              )
          }
          initialWeight={weighPick.initialWeight}
          mode={weighPick.mode}
          qtyStep={weighPick.qtyStep}
          minOrderQty={weighPick.minOrderQty}
          maxOrderQty={weighPick.maxOrderQty}
          onClose={() => { setWeighPick(null); focusScanField(); }}
          onConfirm={(weightKg) => {
            const ctx = weighPick;
            setWeighPick(null);
            addToCart(ctx.item, {
              keepValue: ctx.keepValue,
              lotId: ctx.lotId,
              unitPrice: ctx.unitPrice,
              lotQty: ctx.stockCap,
              mrp: ctx.mrp,
              requiresLot: ctx.requiresLot,
              qty: weightKg,
              soldBy: SOLD_BY.WEIGHT,
              replaceQty: ctx.mode === 'edit',
            });
          }}
        />
      )}

      {historyOpen && (
        <PosHistoryModal
          token={user.idToken}
          canEdit={canEditPosBills}
          visible={!isEditMode && !reprintOpen && !receipt}
          onClose={() => setHistoryOpen(false)}
          onOpenSale={(saleReceipt) => {
            setLastReceipt({
              ...saleReceipt,
              counter: saleReceipt.counter || POS_COUNTER,
              gstin: saleReceipt.gstin || POS_GSTIN,
              cashier_name: saleReceipt.cashier_name || user.name,
            });
            setReprintOpen(true);
          }}
          onEditSale={(salePayload) => {
            beginEditSale(salePayload);
          }}
          onVoided={(voidedReceipt) => {
            reloadProducts();
            refreshSessionStats();
            const confirmed = {
              ...voidedReceipt,
              counter: voidedReceipt.counter || POS_COUNTER,
              gstin: voidedReceipt.gstin || POS_GSTIN,
              cashier_name: voidedReceipt.cashier_name || user.name,
              voided: true,
            };
            setChangeDue(0);
            setLastReceipt(confirmed);
            setReceipt(confirmed);
            notify(`Bill ${confirmed.display_id || ''} voided — refund ${formatCurrency(confirmed.total || 0)}.`);
          }}
        />
      )}

      {amendPreview && (
        <PosAmendSettlementModal
          preview={amendPreview}
          preferredMethod={selectedMethods[0] || editSale?.originalPaymentMethod || ''}
          confirming={amending}
          repreviewing={repreviewing}
          confirmError={amendError}
          onClose={() => {
            if (amending || repreviewing) return;
            setAmendPreview(null);
            setAmendError('');
          }}
          onConfirm={handleConfirmAmend}
        />
      )}

      {receipt && (
        <Receipt
          mode="sale"
          receipt={receipt}
          changeDue={changeDue}
          token={user.idToken}
          products={products}
          onClose={closeReceipt}
        />
      )}

      {reprintOpen && lastReceipt && (
        <Receipt
          mode="reprint"
          receipt={lastReceipt}
          token={user.idToken}
          products={products}
          closeLabel={historyOpen ? 'Back' : 'Close'}
          onClose={() => setReprintOpen(false)}
        />
      )}
    </div>
  );
}

function ProductTile({ product, inCartQty, onAdd }) {
  const byWeight = isSoldByWeight(product);
  const canGapFill = byWeight || product.catalogComplete === false;
  const outOfStock = !canGapFill && product.stock <= inCartQty;
  return (
    <button className={`${styles.tile} ${outOfStock ? styles.tileDisabled : ''}`} onClick={onAdd} disabled={outOfStock}>
      <span className={styles.tileEmoji} style={{ background: `${product.swatch}22`, color: product.swatch }}>
        {product.emoji}
      </span>
      <span className={styles.tileName}>{product.name}</span>
      <span className={styles.tilePrice}>
        {formatPrice(product, formatCurrency)}
      </span>
      {byWeight && <span className={styles.tileWeightBadge}>Weigh</span>}
      {outOfStock ? (
        <span className={styles.tileOutOfStock}>Out of stock</span>
      ) : inCartQty > 0 ? (
        <span className={styles.tileInCart}>
          {byWeight ? `${formatQty(inCartQty, product)} in cart` : `${inCartQty} in cart`}
        </span>
      ) : canGapFill ? (
        <span className={styles.tileStock}>Available at POS</span>
      ) : (
        <span className={styles.tileStock}>{formatStock(product.stock, product)} left</span>
      )}
    </button>
  );
}
